// A STEP OF A SCREEN THAT IS A ROUTE (Session BBX1, design section 7). A viewer or a results step held in component
// state has no history entry, so the Android Back button leaves the whole screen. Here the step lives in the URL's
// query string under one key: opening it PUSHES an entry, so Back (the hardware button or the in-app arrow) takes the
// engineer one step back, and closing it from inside goes back the same way. Flipping within a step (the next page of
// a manual) REPLACES the entry, so it is not a step of its own.
//
// Pure routing, no storage: a reload keeps the URL, and what the step shows is derived from it.
import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export function useRoutedOverlay(key: string): {
  value: string | null;
  open: (value: string) => void;
  replace: (value: string) => void;
  close: () => void;
} {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const value = params.get(key);
  // Whether the entry on top of the history is one this hook pushed, so closing can go BACK rather than leave a
  // stale entry behind. A deep link or a reload is not one of ours: closing replaces the URL instead.
  const pushed = useRef(false);

  // The step went away by itself (Back was pressed): the next close is not ours to undo.
  useEffect(() => {
    if (value === null) pushed.current = false;
  }, [value]);

  const set = useCallback(
    (next: string | null, replaceEntry: boolean) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === null) out.delete(key);
          else out.set(key, next);
          return out;
        },
        { replace: replaceEntry },
      );
    },
    [key, setParams],
  );

  const open = useCallback(
    (next: string) => {
      pushed.current = true;
      set(next, false);
    },
    [set],
  );
  const replace = useCallback((next: string) => set(next, true), [set]);
  const close = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      navigate(-1);
    } else {
      set(null, true);
    }
  }, [navigate, set]);

  return { value, open, replace, close };
}

// The in-app back arrow: history back, or Home when there is no history to go back to (a deep link, a reload).
export function goBackOrHome(navigate: ReturnType<typeof useNavigate>, locationKey: string, home = "/engineer"): void {
  if (locationKey === "default") navigate(home, { replace: true });
  else navigate(-1);
}
